import * as React from "react";
import { HandCoins, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from "@eduviaos/ui-kit";
import {
  PAYMENT_METHOD_LABELS,
  RECOVERY_PLAN_LABELS,
  formatPaise,
  istToday,
  rupeesToPaise,
  shortMonthLabel,
  useCreateAdvance,
  usePreviewAdvance,
  useTeamPayPolicy,
  type AdvancePreview,
  type RecoveryPlan,
  type RecoveryPlanInput,
  type TeamPaymentMethod,
} from "@/lib/teamPay/api";
import { ErrorBanner, Field, NativeSelect, PreviewNote } from "@/components/team-pay/shared";
import { errorMessage } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — give an advance and choose how it comes back out of pay.
 * The EMI preview is the BE's own schedule, so what the owner reads is
 * exactly what the statement will deduct.
 */

const PREVIEW_DEBOUNCE_MS = 300;
const PLANS: RecoveryPlan[] = ["emi", "next_month", "same_month"];

export function AdvanceSheet({
  open,
  onOpenChange,
  payeeId,
  memberName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payeeId: string | null;
  memberName: string;
}) {
  const policy = useTeamPayPolicy(open);
  const [amount, setAmount] = React.useState("");
  const [givenOn, setGivenOn] = React.useState(istToday());
  const [method, setMethod] = React.useState<TeamPaymentMethod>("cash");
  const [plan, setPlan] = React.useState<RecoveryPlan>("emi");
  const [months, setMonths] = React.useState("3");
  const [note, setNote] = React.useState("");
  const [preview, setPreview] = React.useState<AdvancePreview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const previewMutation = usePreviewAdvance();
  const create = useCreateAdvance();
  const { mutateAsync: runPreview } = previewMutation;
  const policyPlan = policy.data?.default_recovery_plan;
  const policyMonths = policy.data?.default_emi_months;

  React.useEffect(() => {
    if (open) {
      setAmount("");
      setGivenOn(istToday());
      setMethod("cash");
      setPlan(policyPlan && policyPlan !== "manual" ? policyPlan : "emi");
      setMonths(String(policyMonths ?? 3));
      setNote("");
      setPreview(null);
      setError(null);
    }
  }, [open, policyPlan, policyMonths]);

  const amountPaise = rupeesToPaise(amount);
  const emiMonths = Math.max(1, Math.min(36, Math.round(Number(months) || 1)));
  const recovery = React.useMemo<RecoveryPlanInput>(
    () => (plan === "emi" ? { plan, emi_months: emiMonths } : { plan }),
    [plan, emiMonths],
  );

  React.useEffect(() => {
    if (!open || amountPaise === null || !givenOn) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      runPreview({ amount_paise: amountPaise, given_on: givenOn, recovery })
        .then((result) => {
          if (!cancelled) {
            setPreview(result);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(errorMessage(err, "Couldn't work out the instalments."));
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, amountPaise, givenOn, recovery, runPreview]);

  const canSubmit = payeeId !== null && amountPaise !== null && !create.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || payeeId === null || amountPaise === null) return;
    setError(null);
    try {
      await create.mutateAsync({
        staff_payee_id: payeeId,
        amount_paise: amountPaise,
        given_on: givenOn,
        method,
        note: note.trim() || null,
        recovery,
      });
      toast.success(`Advance of ${formatPaise(amountPaise)} recorded for ${memberName}.`);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "Couldn't record the advance. Please retry."));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[460px]"
        data-testid="team-advance-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <HandCoins className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">Advance for {memberName}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                Money given ahead of pay. It is taken back from their pay on the schedule you pick.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {error ? <ErrorBanner message={error} /> : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Amount (₹)" htmlFor="tpa-amount" required>
                <Input
                  id="tpa-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  autoFocus
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Given on" htmlFor="tpa-date" required>
                <Input
                  id="tpa-date"
                  type="date"
                  value={givenOn}
                  max={istToday()}
                  onChange={(e) => setGivenOn(e.target.value)}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Take it back" htmlFor="tpa-plan">
                <NativeSelect
                  id="tpa-plan"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value as RecoveryPlan)}
                >
                  {PLANS.map((p) => (
                    <option key={p} value={p}>
                      {RECOVERY_PLAN_LABELS[p]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              {plan === "emi" ? (
                <Field label="Over how many months" htmlFor="tpa-months">
                  <Input
                    id="tpa-months"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={36}
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>

            {preview ? (
              <PreviewNote busy={previewMutation.isPending}>
                <p data-testid="advance-preview-summary">{preview.summary}</p>
                {preview.schedule.length > 1 ? (
                  <ul className="text-muted-foreground mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {preview.schedule.map((s) => (
                      <li key={`${s.year}-${s.month}`} className="flex justify-between gap-2">
                        <span>{shortMonthLabel(s.year, s.month)}</span>
                        <span className="tabular-nums">{formatPaise(s.amount_paise)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </PreviewNote>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Given by" htmlFor="tpa-method">
                <NativeSelect
                  id="tpa-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as TeamPaymentMethod)}
                >
                  {(Object.keys(PAYMENT_METHOD_LABELS) as TeamPaymentMethod[]).map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Note" htmlFor="tpa-note">
                <Input
                  id="tpa-note"
                  value={note}
                  maxLength={500}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={create.isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              Record advance
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
